// Komplain Customer — Phase 1F (H1)
//
// ⛔ LAYAR INI SENGAJA NOL PUNYA TOMBOL YANG MENYENTUH UANG. Nol "setujui
// refund", nol "kembalikan dana". Kalau sebuah komplain berujung refund,
// refund-nya diajukan lewat layar Refund dengan approval-nya sendiri
// (Q95/Q101), lalu nomornya ditautkan di sini.
//
// Kalau layar ini bisa mengeluarkan uang, komplain jadi jalan pintas
// melewati ambang Rp5 juta — dan ambang itu ada justru supaya uang besar nol
// keluar tanpa Owner melihatnya.
//
// ⚠ KATEGORI SENGAJA BEBAS, bukan daftar tertutup. Nol satu pun keputusan
// Owner menetapkan daftarnya, dan daftar yang salah akan MENOLAK KOMPLAIN
// SUNGGUHAN DI DEPAN CUSTOMER. Layar menawarkan saran lewat `datalist` —
// menyarankan, bukan membatasi.
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { StatusBadge, toneFromMeta } from "../components/etams/StatusBadge";
import { BusyButton } from "../components/etams/BusyButton";
import PilihFA from "../components/etams/PilihFA";
import Hint from "../components/etams/Hint";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  MessageSquareWarning, RefreshCw, AlertCircle, Plus, Inbox,
} from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";
import { FA_ISI_ROLES } from "../lib/roles";
import {
  listComplaints, getComplaint, createComplaint, tanganiComplaint,
  selesaikanComplaint, getComplaintMeta, apiErrorMessage, isiAkarComplaint,
} from "../lib/refundApi";

// Label dari daftar {code,label} meta — kode mentah tidak tampil ke staf (K200).
function labelDari(daftar, code) {
  if (!code) return "—";
  return (daftar || []).find(x => x.code === code)?.label || code;
}

function badge(meta, code) {
  const s = (meta?.statuses || []).find(x => x.code === code);
  return {
    label: s?.label || code || "—",
    description: s?.description || "",
    tone: toneFromMeta(s?.tone),
  };
}

export default function Complaints() {
  const [params, setParams] = useSearchParams();
  // K206 / VONIS-HAK-BACA-ISI-FA: nama customer hanya dikirim backend ke Owner/KC/CS — kolomnya tidak digambar
  // untuk Admin Produksi (ia menangani komplain lewat nomor komplain + nomor FA).
  const { profile } = useAuth();
  const isiLengkap = FA_ISI_ROLES.includes(profile?.role?.code);
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  // Paginasi + cari + urut di SERVER (2 Sep 2026). `urutSah` cermin
  // `URUT_SAH` di services/complaint_service.py — `customer_name` dan
  // `fa_number` medan hasil pengayaan, jadi nol bisa diurut server (tapi
  // TETAP bisa DICARI: backend meresolusinya jadi id lebih dulu).
  const [total, setTotal] = useState(0);
  const daftar = useDaftar(rows, {
    server: { total, urutSah: ["created_at", "complaint_number", "status", "kategori", "sumber"] },
    urutAwal: { kunci: "created_at", arah: "desc" },
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const [detail, setDetail] = useState(null);
  const [tutupFor, setTutupFor] = useState(null);
  const [penyelesaian, setPenyelesaian] = useState("");
  const [refundId, setRefundId] = useState("");
  const [menyerah, setMenyerah] = useState(false);
  const [buatOpen, setBuatOpen] = useState(false);
  const [form, setForm] = useState({ sumber: "WHATSAPP" });
  // Bentuk `{fa, wo}` milik PilihFA; `fa_id` di `form` tetap sumber kirimnya.
  const [faTerpilih, setFaTerpilih] = useState(null);

  const status = params.get("status") || "";

  const { cari: kata, limit, offset, sort, arah } = daftar.paramServer;
  const bangunParams = useCallback((ovr = {}) => ({
    ...(status && { status }),
    ...(kata && { cari: kata }),
    limit, offset, sort, arah, ...ovr,
  }), [status, kata, limit, offset, sort, arah]);

  const ambilHalaman = useCallback(async (off, n) => {
    const r = await listComplaints(bangunParams({ offset: off, limit: n }));
    return r.data;
  }, [bangunParams]);

  const muat = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [m, r] = await Promise.all([
        meta ? Promise.resolve({ data: meta }) : getComplaintMeta(),
        listComplaints(bangunParams()),
      ]);
      if (!meta) setMeta(m.data);
      setRows(r.data?.items || []);
      setTotal(typeof r.data?.total === "number" ? r.data.total : (r.data?.items || []).length);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [bangunParams, meta]);

  useEffect(() => { muat(); }, [muat]);

  const izin = meta?.permissions || {};

  const aksi = async (fn, pesan) => {
    setBusy(true);
    try {
      await fn();
      toast.success(pesan);
      setDetail(null); setTutupFor(null); setBuatOpen(false);
      await muat();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const minSelesai = meta?.min_penyelesaian || 3;

  return (
    <div className="space-y-4" data-testid="page-complaints">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <MessageSquareWarning size={20} /> Komplain Customer
          </h1>
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            Keluhan customer dan bagaimana ia diselesaikan.
            <Hint>Kalau berujung refund, ajukan lewat layar Refund Customer lalu tautkan nomornya di sini.</Hint>
          </p>
        </div>
        <div className="flex gap-2">
          {izin.write && (
            <Button onClick={() => {
                      // ⚠ `faTerpilih` DIKOSONGKAN BERSAMA `form`. Keduanya
                      //   menyimpan pilihan yang SAMA dalam dua bentuk;
                      //   mengosongkan satu saja membuat dialog berikutnya
                      //   membuka dengan FA lama masih tergambar padahal
                      //   `fa_id`-nya sudah kosong.
                      setBuatOpen(true);
                      setForm({ sumber: "WHATSAPP" });
                      setFaTerpilih(null);
                    }}
                    data-testid="km-new">
              <Plus size={16} /> Catat komplain
            </Button>
          )}
          <Button variant="outline" onClick={muat} disabled={loading}
                  data-testid="km-refresh">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Muat ulang
          </Button>
        </div>
      </div>

      <Card className="p-3">
        <select className="border rounded-md h-9 px-2 text-sm bg-background"
                value={status}
                onChange={e => {
                  const n = new URLSearchParams(params);
                  if (e.target.value) n.set("status", e.target.value);
                  else n.delete("status");
                  setParams(n, { replace: true });
                }}
                data-testid="km-status-filter">
          <option value="">Semua status</option>
          {(meta?.statuses || []).map(s => (
            <option key={s.code} value={s.code}>{s.label}</option>
          ))}
        </select>
      </Card>

      {err && (
        <Card className="p-4 flex gap-2 items-start text-destructive">
          <AlertCircle size={18} className="mt-0.5 shrink-0" /><span>{err}</span>
        </Card>
      )}

      <Card className="overflow-x-auto">
        <div className="p-3 pb-0">
          <PalangDaftar daftar={daftar} placeholder={isiLengkap ? "Cari nomor / customer / FA / kategori…" : "Cari nomor / FA / kategori…"} testId="km-palang"
                        ekspor={{ nama: "komplain", kolom: [
                          { kunci: "complaint_number", judul: "Nomor" },
                          { kunci: "created_at", judul: "Tanggal", ambil: (c) => formatDate(c.created_at) },
                          ...(isiLengkap ? [{ kunci: "customer_name", judul: "Customer" }] : []),
                          { kunci: "fa_number", judul: "FA" },
                          { kunci: "kategori", judul: "Kategori" },
                          { kunci: "sumber", judul: "Sumber" },
                          { kunci: "status", judul: "Status" },
                          { kunci: "refund_number", judul: "Refund" },
                        ], ambilHalaman }} />
        </div>
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <ThUrut daftar={daftar} kunci="complaint_number" className="p-2">Nomor</ThUrut>
              <ThUrut daftar={daftar} kunci="created_at" className="p-2">Tanggal</ThUrut>
              {isiLengkap && <ThUrut daftar={daftar} kunci="customer_name" className="p-2">Customer</ThUrut>}
              <th className="p-2">FA</th>
              <ThUrut daftar={daftar} kunci="kategori" className="p-2">Kategori</ThUrut>
              <th className="p-2">Sumber</th>
              <ThUrut daftar={daftar} kunci="status" className="p-2">Status</ThUrut>
              <th className="p-2">Refund</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={isiLengkap ? 9 : 8} className="p-6 text-center text-muted-foreground">
                Memuat…</td></tr>
            )}
            {!loading && daftar.total === 0 && (
              <tr><td colSpan={isiLengkap ? 9 : 8} className="p-8 text-center text-muted-foreground">
                <Inbox size={28} className="mx-auto mb-2 opacity-40" />
                {daftar.cari.trim() ? "Tidak ada yang cocok dengan pencarian."
                  : status ? "Tidak ada komplain dengan status ini."
                  : "Belum ada komplain tercatat."}
              </td></tr>
            )}
            {daftar.tampil.map(c => {
              const b = badge(meta, c.status);
              return (
                <tr key={c.id} className="border-t hover:bg-muted/30"
                    data-testid={`km-row-${c.complaint_number}`}>
                  <td className="p-2 font-medium">{c.complaint_number}</td>
                  <td className="p-2">{formatDate(c.created_at)}</td>
                  {isiLengkap && <td className="p-2">{c.customer_name || "—"}</td>}
                  <td className="p-2">{c.fa_number || "—"}</td>
                  <td className="p-2">{labelDari(meta?.kategori, c.kategori)}</td>
                  <td className="p-2">{labelDari(meta?.sumber, c.sumber)}</td>
                  <td className="p-2">
                    <StatusBadge tone={b.tone} title={b.description}>
                      {b.label}
                    </StatusBadge>
                  </td>
                  {/* Tautan refund ditampilkan sebagai NOMOR, bukan tombol —
                      layar ini nol boleh jadi pintu menuju uang. */}
                  <td className="p-2">{c.refund_number || "—"}</td>
                  <td className="p-2 text-right">
                    <Button size="sm" variant="ghost"
                            onClick={async () => {
                              try {
                                const { data } = await getComplaint(c.id);
                                setDetail(data);
                              } catch (e) { toast.error(apiErrorMessage(e)); }
                            }}
                            data-testid={`km-detail-${c.complaint_number}`}>
                      Rincian
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Paginasi daftar={daftar} testId="km-paginasi" />
      </Card>

      {/* ---------------- Rincian ---------------- */}
      <Dialog open={!!detail} onOpenChange={o => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Komplain {detail?.complaint_number}</DialogTitle>
            <DialogDescription>
              {badge(meta, detail?.status).description}
            </DialogDescription>
          </DialogHeader>

          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                {isiLengkap && <div><span className="text-muted-foreground">Customer</span><br />
                  {detail.customer_name || "—"}</div>}
                <div><span className="text-muted-foreground">FA</span><br />
                  {detail.fa_number || "—"}</div>
                <div><span className="text-muted-foreground">Kategori</span><br />
                  {labelDari(meta?.kategori, detail.kategori)}</div>
                <div><span className="text-muted-foreground">Sumber</span><br />
                  {labelDari(meta?.sumber, detail.sumber)}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Keluhan</span>
                <p className="whitespace-pre-wrap">{detail.deskripsi}</p>
              </div>
              {/* Langkah 26 Pareto v1 (ISIAN §6): tahap asal + PIC — diisi saat/sesudah ditangani. */}
              <div className="grid grid-cols-2 gap-2" data-testid="km-akar">
                <label className="text-muted-foreground">Tahap asal masalah
                  <select className="mt-1 w-full rounded-md border bg-background px-2 py-1 text-foreground"
                          disabled={!izin.write || busy} value={detail.tahap_asal || ""}
                          onChange={e => setDetail({ ...detail, tahap_asal: e.target.value || null })}
                          data-testid="km-akar-tahap">
                    <option value="">— belum diisi —</option>
                    {(meta?.tahap || []).map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
                  </select>
                </label>
                <label className="text-muted-foreground">PIC
                  <select className="mt-1 w-full rounded-md border bg-background px-2 py-1 text-foreground"
                          disabled={!izin.write || busy} value={detail.pic_employee_id || ""}
                          onChange={e => setDetail({ ...detail, pic_employee_id: e.target.value || null })}
                          data-testid="km-akar-pic">
                    <option value="">— belum diisi —</option>
                    {(meta?.pic || []).map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
                  </select>
                </label>
              </div>
              {detail.penyelesaian && (
                <div className="rounded-md border p-2 bg-muted/30">
                  <span className="text-muted-foreground">Penyelesaian</span>
                  <p className="whitespace-pre-wrap">{detail.penyelesaian}</p>
                  {detail.refund_number && (
                    <p className="mt-1">Bertaut refund{" "}
                      <strong>{detail.refund_number}</strong></p>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2">
            {detail && detail.status === "BARU" && izin.write && (
              <BusyButton busy={busy}
                          onClick={() => aksi(() => tanganiComplaint(detail.id),
                                              "Komplain ditangani.")}
                          data-testid="km-tangani">Tangani</BusyButton>
            )}
            {detail && !["SELESAI", "DITUTUP"].includes(detail.status) && izin.write && (
              <Button onClick={() => {
                        setTutupFor(detail); setPenyelesaian("");
                        setRefundId(""); setMenyerah(false);
                      }}
                      data-testid="km-selesai-btn">Selesaikan</Button>
            )}
            {detail && izin.write && (
              <BusyButton busy={busy} variant="outline"
                          onClick={() => aksi(() => isiAkarComplaint(detail.id, {
                            tahap_asal: detail.tahap_asal || null,
                            pic_employee_id: detail.pic_employee_id || null,
                          }), "Akar masalah disimpan.")}
                          data-testid="km-akar-simpan">Simpan akar masalah</BusyButton>
            )}
            <Button variant="outline" onClick={() => setDetail(null)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------- Selesaikan ---------------- */}
      <Dialog open={!!tutupFor} onOpenChange={o => !o && setTutupFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Selesaikan {tutupFor?.complaint_number}</DialogTitle>
            <DialogDescription>
              Tuliskan bagaimana komplain ini diselesaikan.{" "}
              <Hint>Catatan ini dipakai untuk melihat pola komplain. Komplain yang ditutup tanpa catatan tidak mengajari apa pun.</Hint>
            </DialogDescription>
          </DialogHeader>

          <Textarea value={penyelesaian}
                    onChange={e => setPenyelesaian(e.target.value)}
                    placeholder={`Penyelesaian (min. ${minSelesai} karakter)`}
                    data-testid="km-penyelesaian" />

          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={menyerah} className="mt-1"
                   onChange={e => setMenyerah(e.target.checked)}
                   data-testid="km-ditutup" />
            <span>
              Tutup tanpa penyelesaian lanjutan{" "}
              <Hint>Untuk customer yang tidak merespons atau keluhan di luar tanggung jawab ETAMS. Berbeda dari "Selesai": komplain ini ditutup tanpa terselesaikan.</Hint>
            </span>
          </label>

          <Input value={refundId} onChange={e => setRefundId(e.target.value)}
                 placeholder="ID refund (opsional) — refund harus SUDAH diajukan"
                 data-testid="km-refund-id" />
          <p className="text-xs text-muted-foreground">
            ⚠ Layar ini tidak bisa membuat refund. Ajukan lebih dulu di layar
            Refund Customer supaya melewati persetujuannya sendiri, lalu
            tempel ID-nya di sini.
          </p>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTutupFor(null)}>Urung</Button>
            <BusyButton busy={busy}
                        disabled={penyelesaian.trim().length < minSelesai}
                        onClick={() => aksi(() => selesaikanComplaint(tutupFor.id, {
                          penyelesaian: penyelesaian.trim(),
                          ditutup: menyerah,
                          refund_id: refundId.trim() || null,
                        }), menyerah ? "Komplain ditutup." : "Komplain selesai.")}
                        data-testid="km-selesai-confirm">
              {menyerah ? "Tutup" : "Selesaikan"}
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------- Catat ---------------- */}
      <Dialog open={buatOpen} onOpenChange={o => !o && setBuatOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Catat komplain</DialogTitle>
            <DialogDescription>
              Wajib menunjuk FA atau permintaan desain — komplain tanpa induk
              tidak bisa ditindaklanjuti siapa pun.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            {/* ⛔ DULU DUA KOTAK TEKS yang meminta UUID 36 karakter diketik
                tangan. Diganti 5 September 2026 dengan komponen yang SUDAH
                ADA sejak 2 September — `PilihFA` menutup cacat yang sama di
                Serah Terima Tahap, lalu berhenti di satu layar itu.

                ⚠ Sapuan 5 Sep menemukan EMPAT ruas UUID yang tersisa, dan
                nol satu pun mesin bisa menanyakan "apakah perbaikan ini
                sudah disebar?" — suite hijau, gerbang hijau, build hijau.

                `denganWo={false}`: komplain nol peduli WO. Tanpa itu
                komponennya memasang peringatan "FA ini belum punya WO" pada
                alur yang memang nol pernah membutuhkannya. */}
            <div>
              <span className="mb-1 block text-xs font-medium text-muted-foreground">
                Pesanan (FA) yang dikomplain
              </span>
              <PilihFA
                value={faTerpilih}
                denganWo={false}
                testId="km-pilih-fa"
                onChange={(v) => {
                  setFaTerpilih(v);
                  setForm(f => ({ ...f, fa_id: v?.fa?.id || null }));
                }}
              />
            </div>
            <Input placeholder="atau ID Permintaan Desain (DR)"
                   value={form.design_request_id || ""}
                   onChange={e => setForm(f => ({ ...f, design_request_id: e.target.value }))}
                   data-testid="km-dr" />
            {/* ⚠ Ruas DR SENGAJA masih kotak teks. `PilihFA` mencari FA lewat
                `/tracking/cari`; nol ada padanannya untuk DR, dan membuatnya
                adalah unit kerja tersendiri — bukan sesuatu yang diselipkan
                ke sapuan ini. Dicatat di BACKLOG apa adanya. */}

            {/* K182: kategori = master terkunci (daftar dari `/meta`, dikelola Owner di
                Master › Kategori Komplain). Tidak cocok mana pun → "Lainnya". */}
            <select className="w-full border rounded-md h-9 px-2 bg-background"
                    value={form.kategori || ""}
                    onChange={e => setForm(f => ({ ...f, kategori: e.target.value }))}
                    data-testid="km-kategori">
              <option value="">— pilih kategori —</option>
              {(meta?.kategori || []).map(k => (
                <option key={k.code} value={k.code}>{k.label}</option>
              ))}
            </select>

            <select className="w-full border rounded-md h-9 px-2 bg-background"
                    value={form.sumber || "WHATSAPP"}
                    onChange={e => setForm(f => ({ ...f, sumber: e.target.value }))}
                    data-testid="km-sumber">
              {(meta?.sumber || []).map(s => (
                <option key={s.code} value={s.code}>{s.label}</option>
              ))}
            </select>

            <Textarea placeholder={`Keluhan customer (min. ${meta?.min_deskripsi || 5} karakter)`}
                      value={form.deskripsi || ""}
                      onChange={e => setForm(f => ({ ...f, deskripsi: e.target.value }))}
                      data-testid="km-deskripsi" />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBuatOpen(false)}>Urung</Button>
            <BusyButton busy={busy}
                        disabled={(!form.fa_id && !form.design_request_id)
                                  || !(form.kategori || "").trim()
                                  || (form.deskripsi || "").trim().length < (meta?.min_deskripsi || 5)}
                        onClick={() => aksi(() => createComplaint({
                          kategori: (form.kategori || "").trim(),
                          deskripsi: (form.deskripsi || "").trim(),
                          sumber: form.sumber || "LAIN",
                          fa_id: form.fa_id || null,
                          design_request_id: form.design_request_id || null,
                        }), "Komplain tercatat.")}
                        data-testid="km-submit">Catat</BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
